/* eslint-disable */
import React, { useMemo, useState, useEffect, useCallback, useContext } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	FormControlLabel,
	Checkbox,
	TextField,
	Typography,
	Box,
	Button,
	IconButton,
	Select,
	InputLabel,
	FormControl,
	Chip,
	OutlinedInput,
	Tabs,
	Tab
} from "@mui/material";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CloseIcon from "@material-ui/icons/Close";
import { trackPromise } from "react-promise-tracker";
import { api } from "../../../../../utils/api";
import { FormGroup, ListItemText, MenuItem } from "@material-ui/core";
import isEmployerRoute from "../../../../../utils/commonFunctions/isEmployerRoute";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { useEmployerRoute } from "../../../../../hooks";
import { set } from "date-fns";
import { AuthContext } from "../../../../../contexts";
import { filterByFirstTrue, getPermissionsFromGroupOwner } from "../../../../../utils/getPermissionsFromGroupOwner";

type PermissionNode = {
	key: string;
	label: string;
	children?: PermissionNode[];
};

const deepEqual = (a: any, b: any): boolean => {
	return JSON.stringify(a) === JSON.stringify(b);
};

const mockReportListOptions = [
	{ _id: 1, name: "Tiger 1" },
	{ _id: 2, name: "Tiger 2" },
	{ _id: 3, name: "Tiger 3" }
];

function extractCheckedKeys(obj: any, prefix = ""): string[] {
	let checkedKeys: string[] = [];
	Object.entries(obj).forEach(([key, value]) => {
		const path = prefix ? `${prefix}.${key}` : key;
		if (value === true) {
			checkedKeys.push(path);
		} else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
			checkedKeys = checkedKeys.concat(extractCheckedKeys(value, path));
		}
	});
	return checkedKeys;
}

function buildTree(obj: any, prefix = ""): PermissionNode[] {
	return Object.entries(obj)
		.filter(([key]) => key !== "role_based_menu_view" && key !== "report_lists")
		.map(([key, value]) => {
			const path = prefix ? `${prefix}.${key}` : key;
			const node: PermissionNode = { key: path, label: key };
			if (value && typeof value === "object" && !Array.isArray(value)) {
				// node.children = buildTree(value, path);
				const orderPriority = ["view", "create", "delete", "update"];

				node.children = buildTree(value, path).sort((a, b) => {
					const labelA = a.label?.toLowerCase?.() ?? "";
					const labelB = b.label?.toLowerCase?.() ?? "";

					const indexA = orderPriority.indexOf(labelA);
					const indexB = orderPriority.indexOf(labelB);

					// If both are in priority list, maintain that order
					if (indexA !== -1 && indexB !== -1) return indexA - indexB;

					// If only A is in priority list, it goes first
					if (indexA !== -1) return -1;

					// If only B is in priority list, it goes first
					if (indexB !== -1) return 1;

					// Otherwise, sort alphabetically
					return labelA.localeCompare(labelB);
				});
			}
			return node;
		});
}

function updateSchemaWithSelectedKeys(
	original: any,
	selected: string[],
	selectedEmployerReportList: any[],
	selectedReportList: any[]
): any {
	const updated = JSON.parse(JSON.stringify(original));

	function applySelection(obj: any, prefix = "") {
		Object.entries(obj).forEach(([key, value]) => {
			const path = prefix ? `${prefix}.${key}` : key;
			if (typeof value === "object" && value !== null && !Array.isArray(value)) {
				applySelection(obj[key], path);
			} else {
				obj[key] = selected.includes(path);
			}
		});
	}

	applySelection(updated);

	// Set report_lists explicitly
	if (updated.reports_menu?.self) {
		updated.reports_menu.self.report_lists = selectedReportList;
	}

	// Ensure super_admin array exists, even if user didn’t touch it
	if (!updated.role_based_menu_view) {
		updated.role_based_menu_view = {};
	}
	if (!updated.role_based_menu_view.menu_lists) {
		updated.role_based_menu_view.menu_lists = {};
	}
	if (!Array.isArray(updated.role_based_menu_view.menu_lists.super_admin)) {
		updated.role_based_menu_view.menu_lists.super_admin =
			original.role_based_menu_view?.menu_lists?.super_admin || [];
	}
	if (!Array.isArray(updated.role_based_menu_view.menu_lists.employer)) {
		updated.role_based_menu_view.menu_lists.employer = original.role_based_menu_view?.menu_lists?.employer || [];
	}
	if (!Array.isArray(updated.role_based_menu_view.menu_lists.admin)) {
		updated.role_based_menu_view.menu_lists.admin = original.role_based_menu_view?.menu_lists?.admin || [];
	}
	if (!Array.isArray(updated.employer_reports_menu.self)) {
		updated.employer_reports_menu.self.report_lists = selectedEmployerReportList;
	}

	return updated;
}

interface Props {
	open: boolean;
	onClose: () => void;
	selected: string[];
	setSelected: React.Dispatch<React.SetStateAction<string[]>>;
	permissionsSchema: any;
	userId: string;
	userName: string;
	groupNumberAssignedList: any[];
}

const AssignPermissionDialog: React.FC<Props> = ({
	open,
	onClose,
	selected,
	setSelected,
	permissionsSchema,
	userId,
	userName,
	groupNumberAssignedList
}) => {
	const [search, setSearch] = useState("");
	const [expandedKey, setExpandedKey] = useState<string | false>(false);
	const [reportListValues, setReportListValues] = useState<any[]>([]);
	const [reportDialogOpen, setReportDialogOpen] = useState(false);
	const [reportSearch, setReportSearch] = useState<string>("");

	const [employerReportDialogOpen, setEmployerReportDialogOpen] = useState(false);
	const [employerReportSearch, setEmployerReportSearch] = useState("");

	const [assignedGroupListPermissions, setAssignedGroupListPermissions] = useState<any[]>([]);

	const [filteredTree, setFilteredTree] = useState<any[]>([]);

	const [selectedGroupNumber, setSelectedGroupNumber] = useState<number | null>(null);
	const [selectedReportList, setSelectedReportList] = useState<any[]>([]);
	const [selectedEmployerReportList, setSelectedEmployerReportList] = useState<any[]>([]);
	const [employerReportList, setEmployerReportList] = useState<any[]>([]);
	const [reportList, setReportList] = useState<any[]>([]);

	const [reportActiveTab, setReportActiveTab] = useState<number>(0);
	const [employerReportActiveTab, setEmployerReportActiveTab] = useState<number>(0);

	const queryGroupNumber = useRouteParam("gn");
	const isEmployer = isEmployerRoute();

	const displayName = userName || "User";

	const employerRoute = useEmployerRoute();

	const { user, setUser } = useContext(AuthContext);

	const loginnedUserPermission =
		user && employerRoute && queryGroupNumber
			? getPermissionsFromGroupOwner(user, Number(queryGroupNumber))
			: user
			? getPermissionsFromGroupOwner(user, 0)
			: {};

	const filteredReportList = mockReportListOptions.filter((opt) =>
		opt.name.toLowerCase().includes(reportSearch.toLowerCase())
	);

	// const filteredReportList: any = Object.keys(loginnedUserPermission).length
	// 	? loginnedUserPermission?.report_menu.self.report_lists?.filter((opt: any) =>
	// 			opt.name.toLowerCase().includes(reportSearch.toLowerCase())
	// 	  )
	// 	: [];
	// const filteredEmployerReportList: any = Object.keys(loginnedUserPermission).length
	// 	? loginnedUserPermission?.employer_report_menu.self.report_lists?.filter((opt: any) =>
	// 			opt.name.toLowerCase().includes(reportSearch.toLowerCase())
	// 	  )
	// 	: [];

	const filterPermissionsByGroupNumberAssigned = useCallback(() => {
		if (employerRoute) {
			if (!queryGroupNumber) return [];
			const permission = permissionsSchema?.filter(
				(permission: any) => permission.group_number === queryGroupNumber
			);
			// console.log(permission[0]);
			if (!permission || permission.length === 0) {
				alert("No permissions found for this group number.");
				return [];
			}
			setAssignedGroupListPermissions([
				{
					group_number: queryGroupNumber,
					permissions: permission[0].permission_and_subscription.permissions || {},
					permission_id: permission[0].permission_id
				}
			]);
		} else {
			const groupNumber = new Set((user as any)?.permissions.map((group: any) => group.group_number));
			const assignedGroupPermissions: any = [];
			permissionsSchema?.forEach((permission: any) => {
				console.log("test", permission);
				if (groupNumber.has(permission.group_number)) {
					if (permission.group_number === 0) {
						assignedGroupPermissions.push({
							group_number: permission.group_number,
							permissions: permission.permission_and_subscription.permissions || {},
							permission_id: permission.permission_id
						});
					} else {
						assignedGroupPermissions.push({
							group_number: permission.group_number,
							permissions: permission.permission_and_subscription.permissions || {},
							permission_id: permission.permission_id
						});
					}
				}
			});
			setAssignedGroupListPermissions(assignedGroupPermissions);
		}
	}, [groupNumberAssignedList, permissionsSchema]);

	// const getAllReportList = useCallback(async () => {
	// 	try {
	// 		const response = await fetch("/api/reports");
	// 		const data = await response.json();
	// 		setAllReportList(data);
	// 	} catch (error) {
	// 		console.error("Error fetching report list:", error);
	// 	}
	// }, []);

	const getReports = useCallback(async () => {
		try {
			if (selectedGroupNumber == 0 || selectedGroupNumber) {
				// console.log("employer", selectedGroupNumber);

				const groupNumber = selectedGroupNumber;
				const { employerReports, reports } = await api.reports.getReports({
					group_number: Number(groupNumber)
				});
				setEmployerReportList(employerReports);
				setReportList(reports);
			} else {
				// console.log("not employer", selectedGroupNumber);
				// const { employerReports, reports } = await api.reports.getReports({ group_number: 0 });
				// setReportList(reports);
				// setEmployerReportList(employerReports);
			}
		} catch (error) {
			console.error("Error fetching reports:", error);
			alert("No Reports Found");
		}
	}, [selectedGroupNumber, isEmployer]);

	const tree = useCallback(() => {
		let permission =
			(selectedGroupNumber == 0 || selectedGroupNumber) && assignedGroupListPermissions.length > 0
				? assignedGroupListPermissions.filter((group) => group.group_number == selectedGroupNumber)[0]
						?.permissions // clone to ensure reference change
				: {};

		permission = filterByFirstTrue(loginnedUserPermission, permission);

		// console.log(selectedGroupNumber, "permissions", permission);
		const nodeList = buildTree(permission);
		// console.log("rendering", nodeList);
		return nodeList;
	}, [loginnedUserPermission, selectedGroupNumber, assignedGroupListPermissions]);

	const _toggle = (key: string) => {
		// console.log(selected, "object", key);
		if (selected.includes(key)) {
			// uncheck all children keys
			const keysToUncheck = filteredTree;

			if (key.endsWith(".view")) {
				const parentKey = key.slice(0, -5); // remove ".view"
				const updateKey = parentKey + ".update";
				const createKey = parentKey + ".create";
				const deleteKey = parentKey + ".delete";
				setSelected((prev) =>
					prev.filter((k) => k !== updateKey && k !== createKey && k !== deleteKey && k !== key)
				);
			} else {
				setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
			}
		} else {
			setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
		}
	};
	const matchesSearch = (label: string) => {
		// console.log(label, "searching", search);

		return label.toLowerCase().includes(search.toLowerCase());
	};

	const handleAccordionChange = (key: string, isTopLevel: boolean) => (_: any, isExpanded: boolean) => {
		if (isTopLevel) {
			setExpandedKey(isExpanded ? key : false);
		}
	};

	const employerReportsByType: Record<string, typeof employerReportList> = employerReportList.reduce(
		(acc, report) => {
			if (!acc[report.report_type]) acc[report.report_type] = [];
			acc[report.report_type].push(report);
			return acc;
		},
		{} as Record<string, typeof employerReportList>
	);

	const employerReportTypes = Object.keys(employerReportsByType);

	const reportsByType: Record<string, typeof reportList> = reportList.reduce((acc, report) => {
		if (!acc[report.report_type]) acc[report.report_type] = [];
		acc[report.report_type].push(report);
		return acc;
	}, {} as Record<string, typeof reportList>);

	const reportTypes = Object.keys(reportsByType);

	const nodeMatchesSearch = (node: PermissionNode, search: string): boolean => {
		if (!search) return true;

		const lowerSearch = search.toLowerCase();

		// check current label
		if (node.label.split("_").join(" ").toLowerCase().includes(lowerSearch)) {
			return true;
		}

		// check children recursively
		if (node.children) {
			return node.children.some((child) => nodeMatchesSearch(child, search));
		}

		return false;
	};

	const renderRecursive = (node: PermissionNode, level = 0): JSX.Element | null => {
		// if (node.key === "reports_menu.self") {
		// 	console.log("test", reportList);
		// }
		if (node.label === "self") {
			return (
				<React.Fragment key={node.key}>
					{node.children?.map((child) => renderRecursive(child, level + 1))}
					{node.key === "reports_menu.self" && (
						<Box mt={2} ml={2} display="flex" alignItems="center" gap={2}>
							<Typography variant="subtitle2" gutterBottom>
								Report Lists
							</Typography>
							<Button
								variant="outlined"
								onClick={() => setReportDialogOpen(true)}
								sx={{
									color: "#359a4c",
									borderColor: "#359a4c",
									"&:hover": {
										backgroundColor: "rgba(53, 154, 76, 0.1)",
										borderColor: "#359a4c"
									}
								}}
							>
								{selectedReportList.length > 0
									? `${selectedReportList.length} Reports Selected`
									: "Select Reports"}
							</Button>

							<Dialog
								open={reportDialogOpen}
								onClose={() => setReportDialogOpen(false)}
								// maxWidth="sm"
								PaperProps={{
									sx: {
										width: "800px", // fixed width
										maxWidth: "90vw", // responsive max width
										height: "600px" // fixed height
									}
								}}
								fullWidth
							>
								<DialogTitle
									sx={{
										m: 0,
										p: 2,
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center"
									}}
								>
									Select Reports
									<IconButton
										aria-label="close"
										onClick={() => setReportDialogOpen(false)}
										sx={{
											color: (theme) => theme.palette.grey[500]
										}}
									>
										<CloseIcon />
									</IconButton>
								</DialogTitle>

								<DialogContent>
									<TextField
										fullWidth
										placeholder="Search reports..."
										value={reportSearch}
										onChange={(e) => setReportSearch(e.target.value)}
										margin="normal"
									/>

									{/* Tabs */}
									<Tabs
										value={reportActiveTab}
										onChange={(_, newValue) => setReportActiveTab(newValue)}
										variant="scrollable"
										scrollButtons="auto"
									>
										{reportTypes.map((type) => (
											<Tab key={type} label={type} />
										))}
									</Tabs>

									{/* Reports list for current tab */}
									{reportTypes.map((type, idx) => {
										const currentReports = reportsByType[type] ?? []; // ✅ Safe fallback
										const isActive = reportActiveTab === idx;

										if (!isActive) return null;

										const filteredReports = currentReports.filter((r) =>
											r.report_name.toLowerCase().includes(reportSearch.toLowerCase())
										);

										return (
											<FormGroup
												key={type}
												style={{ maxHeight: 300, overflowY: "auto", marginTop: 8 }}
											>
												{/* Select All */}
												<FormControlLabel
													control={
														<Checkbox
															checked={
																filteredReports.length > 0 &&
																filteredReports.every((r) =>
																	selectedReportList.includes(r.report_id.toString())
																)
															}
															indeterminate={
																filteredReports.some((r) =>
																	selectedReportList.includes(r.report_id.toString())
																) &&
																!filteredReports.every((r) =>
																	selectedReportList.includes(r.report_id.toString())
																)
															}
															onChange={(e) => {
																setSelectedReportList((prev) =>
																	e.target.checked
																		? [
																				...prev,
																				...filteredReports
																					.map((r) => r.report_id.toString())
																					.filter((id) => !prev.includes(id))
																		  ]
																		: prev.filter(
																				(id) =>
																					!filteredReports
																						.map((r) =>
																							r.report_id.toString()
																						)
																						.includes(id)
																		  )
																);
															}}
															sx={{
																"&.Mui-checked": { color: "#359a4c" },
																"&.MuiCheckbox-indeterminate": { color: "#359a4c" }
															}}
														/>
													}
													label="Select All"
												/>
												{/* Individual reports */}
												{filteredReports.map((report) => (
													<FormControlLabel
														key={report.report_id}
														control={
															<Checkbox
																checked={selectedReportList.includes(
																	report.report_id.toString()
																)}
																onChange={() => {
																	setSelectedReportList((prev) =>
																		prev.includes(report.report_id.toString())
																			? prev.filter(
																					(id) =>
																						id !==
																						report.report_id.toString()
																			  )
																			: [...prev, report.report_id.toString()]
																	);
																}}
																sx={{ "&.Mui-checked": { color: "#359a4c" } }}
															/>
														}
														label={report.report_name}
													/>
												))}
											</FormGroup>
										);
									})}

									<Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
										<Button onClick={() => setSelectedReportList([])}>Clear</Button>
										<Button
											variant="contained"
											className="button-green"
											onClick={() => setReportDialogOpen(false)}
										>
											Apply
										</Button>
									</Box>
								</DialogContent>
							</Dialog>
						</Box>
					)}
					{node.key === "employer_reports_menu.self" && (
						<Box mt={2} ml={2} display="flex" alignItems="center" gap={2}>
							<Typography variant="subtitle2" gutterBottom>
								Employer Report Lists
							</Typography>
							<Button
								variant="outlined"
								onClick={() => setEmployerReportDialogOpen(true)}
								sx={{
									color: "#359a4c",
									borderColor: "#359a4c",
									"&:hover": {
										backgroundColor: "rgba(53, 154, 76, 0.1)",
										borderColor: "#359a4c"
									}
								}}
							>
								{selectedEmployerReportList.length > 0
									? `${selectedEmployerReportList.length} Reports Selected`
									: "Select Reports"}
							</Button>

							<Dialog
								open={employerReportDialogOpen}
								onClose={() => setEmployerReportDialogOpen(false)}
								// maxWidth="sm"
								PaperProps={{
									sx: {
										width: "800px", // fixed width
										maxWidth: "90vw", // responsive max width
										height: "600px" // fixed height
									}
								}}
								fullWidth
							>
								<DialogTitle
									sx={{
										m: 0,
										p: 2,
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center"
									}}
								>
									Select Reports
									<IconButton
										aria-label="close"
										onClick={() => setEmployerReportDialogOpen(false)}
										sx={{ color: (theme) => theme.palette.grey[500] }}
									>
										<CloseIcon />
									</IconButton>
								</DialogTitle>

								<DialogContent>
									<TextField
										fullWidth
										placeholder="Search reports..."
										value={employerReportSearch}
										onChange={(e) => setEmployerReportSearch(e.target.value)}
										margin="normal"
									/>

									{/* Tabs */}
									<Tabs
										value={employerReportActiveTab}
										onChange={(_, newValue) => setEmployerReportActiveTab(newValue)}
										variant="scrollable"
										scrollButtons="auto"
									>
										{employerReportTypes.map((type) => (
											<Tab key={type} label={type} />
										))}
									</Tabs>

									{/* Reports list for current tab */}
									{employerReportTypes.map((type, idx) => {
										const currentReports = employerReportsByType[type] ?? []; // ✅ Safe fallback
										const isActive = employerReportActiveTab === idx;

										if (!isActive) return null;

										const filteredReports = currentReports.filter((r) =>
											r.report_name.toLowerCase().includes(employerReportSearch.toLowerCase())
										);

										return (
											<FormGroup
												key={type}
												style={{ maxHeight: 300, overflowY: "auto", marginTop: 8 }}
											>
												{/* Select All */}
												<FormControlLabel
													control={
														<Checkbox
															checked={
																filteredReports.length > 0 &&
																filteredReports.every((r) =>
																	selectedEmployerReportList.includes(
																		r.report_id.toString()
																	)
																)
															}
															indeterminate={
																filteredReports.some((r) =>
																	selectedEmployerReportList.includes(
																		r.report_id.toString()
																	)
																) &&
																!filteredReports.every((r) =>
																	selectedEmployerReportList.includes(
																		r.report_id.toString()
																	)
																)
															}
															onChange={(e) => {
																setSelectedEmployerReportList((prev) =>
																	e.target.checked
																		? [
																				...prev,
																				...filteredReports
																					.map((r) => r.report_id.toString())
																					.filter((id) => !prev.includes(id))
																		  ]
																		: prev.filter(
																				(id) =>
																					!filteredReports
																						.map((r) =>
																							r.report_id.toString()
																						)
																						.includes(id)
																		  )
																);
															}}
															sx={{
																"&.Mui-checked": { color: "#359a4c" },
																"&.MuiCheckbox-indeterminate": { color: "#359a4c" }
															}}
														/>
													}
													label="Select All"
												/>

												{/* Individual reports */}
												{filteredReports.map((report) => (
													<FormControlLabel
														key={report.report_id}
														control={
															<Checkbox
																checked={selectedEmployerReportList.includes(
																	report.report_id.toString()
																)}
																onChange={() => {
																	setSelectedEmployerReportList((prev) =>
																		prev.includes(report.report_id.toString())
																			? prev.filter(
																					(id) =>
																						id !==
																						report.report_id.toString()
																			  )
																			: [...prev, report.report_id.toString()]
																	);
																}}
																sx={{ "&.Mui-checked": { color: "#359a4c" } }}
															/>
														}
														label={report.report_name}
													/>
												))}
											</FormGroup>
										);
									})}

									{/* Footer buttons */}
									<Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
										<Button onClick={() => setSelectedEmployerReportList([])}>Clear</Button>
										<Button
											variant="contained"
											className="button-green"
											onClick={() => setEmployerReportDialogOpen(false)}
										>
											Apply
										</Button>
									</Box>
								</DialogContent>
							</Dialog>
						</Box>
					)}
				</React.Fragment>
			);
		}

		const isLeaf = !node.children || node.children.length === 0;
		const isTopLevel = level === 0;
		if (isLeaf) {
			// if (search && !matchesSearch(node.key)) return null;
			// console.log(node.key);
			return (
				<FormControlLabel
					key={node.key}
					control={
						<Checkbox
							checked={selected.includes(node.key)}
							onChange={() => _toggle(node.key)}
							sx={{
								"&.Mui-checked": {
									color: "#359a4c"
								}
							}}
						/>
					}
					label={<Typography sx={{ fontWeight: "normal" }}>{node.label}</Typography>}
				/>
			);
		}

		const visibleChildren = node.children
			?.map((child) => renderRecursive(child, level + 1))
			.filter(Boolean) as JSX.Element[];

		// if (search && visibleChildren.length === 0) return null;
		if (search && !nodeMatchesSearch(node, search)) return null;
		// console.log(selected, search, "rendering", node.key, visibleChildren);
		return (
			<Accordion
				key={node.key}
				expanded={isTopLevel ? expandedKey === node.key : undefined}
				// style={{ display: isTopLevel && !node.key.includes(search) ? "none" : undefined }}
				onChange={handleAccordionChange(node.key, isTopLevel)}
			>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<Typography sx={{ fontWeight: isTopLevel ? "bold" : "normal" }}>
						{node.label
							.split("_")
							.filter((word) => !["menu", "tab"].includes(word)) // skip ignored words
							.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
							.join(" ")}
					</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<Box
						width="100%"
						sx={{
							display: "flex",
							flexWrap: "wrap",
							gap: 2,
							alignItems: "flex-start"
						}}
					>
						{visibleChildren}
					</Box>
				</AccordionDetails>
			</Accordion>
		);
	};

	const handleSave = useCallback(async () => {
		const permissionWIthId =
			(selectedGroupNumber == 0 || selectedGroupNumber) && assignedGroupListPermissions.length > 0
				? assignedGroupListPermissions.filter((group) => group.group_number == selectedGroupNumber)[0]
				: // clone to ensure reference change
				  {};

		const permission = permissionWIthId?.permissions;

		// console.log(permissionWIthId);

		if (!permission) {
			alert("no permission update");
			return;
		}
		const updatedSchema = updateSchemaWithSelectedKeys(
			permission,
			selected,
			selectedEmployerReportList,
			selectedReportList
		);

		if (deepEqual(updatedSchema, permission)) {
			alert("No changes made.");
			return;
		}

		const finalPayload = { permissions: updatedSchema };

		try {
			// console.log(finalPayload);
			const response = await trackPromise(
				api.groupOwner.updateUserPermissionManagement(finalPayload, permissionWIthId.permission_id)
			);
			if (response) {
				alert("Permissions updated successfully!");
			}
		} catch (err) {
			console.error(err);
			alert("Failed to update permissions");
		}
	}, [
		permissionsSchema,
		selected,
		reportListValues,
		userId,
		selectedGroupNumber,
		selectedEmployerReportList,
		selectedReportList
	]);

	const handleGroupNumberChange = (event: any) => {
		const selectedGroupNumber = event.target.value as number;
		// console.log(selectedGroupNumber);
		setSelectedGroupNumber(selectedGroupNumber);
		// filterPermissionsByGroupNumberAssigned();
	};

	const handlePermissionModelCloseClick = useCallback(() => {
		setSelectedGroupNumber(null);
		onClose();
	}, [selectedGroupNumber]);

	useEffect(() => {
		const filteredTree = tree().filter((node) => node.label !== "self");
		let permission =
			(selectedGroupNumber == 0 || selectedGroupNumber) && assignedGroupListPermissions.length > 0
				? assignedGroupListPermissions.filter((group) => group.group_number == selectedGroupNumber)[0]
						?.permissions // clone to ensure reference change
				: {};
		setSelectedReportList(permission.reports_menu?.self?.report_lists || []);
		setSelectedEmployerReportList(permission.employer_reports_menu?.self?.report_lists || []);
		// console.log("object", filteredTree);
		setFilteredTree(filteredTree);
	}, [tree, assignedGroupListPermissions]);

	useEffect(() => {
		if (permissionsSchema && (selectedGroupNumber == 0 || selectedGroupNumber)) {
			const permission =
				(selectedGroupNumber == 0 || selectedGroupNumber) && assignedGroupListPermissions.length > 0
					? assignedGroupListPermissions.filter((group) => group.group_number == selectedGroupNumber)[0]
							?.permissions // clone to ensure reference change
					: {};
			if (!permission) {
				alert("no permission allowed");
				return;
			}
			const checked = extractCheckedKeys(permission);
			// console.log("checked", checked);
			setSelected(checked);

			getReports();

			// if (
			// 	permission.reports_menu?.self?.report_lists &&
			// 	Array.isArray(permission.reports_menu.self.report_lists)
			// ) {
			// 	setReportListValues(permission.reports_menu.self.report_lists);
			// }
		}
	}, [permissionsSchema, selectedGroupNumber]);

	useEffect(() => {
		if (permissionsSchema) {
			filterPermissionsByGroupNumberAssigned();
		}
	}, [permissionsSchema, queryGroupNumber, isEmployer]);

	useEffect(() => {
		if (open) {
			setExpandedKey(false);
			setSearch("");
			setReportSearch("");
			setReportDialogOpen(false);
			if (!permissionsSchema?.reports_menu?.self?.report_lists) {
				setReportListValues([]);
			}
		}
	}, [open, userId]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					{/* <Typography variant="h6">Select Permissions for {displayName}</Typography> */}
					<Box>
						<FormControl sx={{ m: 1, mx: "0", minWidth: "200px" }}>
							<InputLabel id="demo-simple-select-label">Group Number</InputLabel>
							<Select
								labelId="demo-simple-select-label"
								id="demo-simple-select"
								value={selectedGroupNumber}
								label="Group Number"
								onChange={handleGroupNumberChange}
							>
								{assignedGroupListPermissions &&
									assignedGroupListPermissions.map((group) => {
										if (group.group_number === 0) {
											return (
												<MenuItem key={group.id} value={group.group_number}>
													{"Super Admin"}
												</MenuItem>
											);
										} else {
											return (
												<MenuItem key={group.id} value={group.group_number}>
													{group.group_number}
												</MenuItem>
											);
										}
									})}
								{/* <MenuItem value={0}>0 - super admin</MenuItem>
								<MenuItem value={220}>220 - Brookshire Brothers</MenuItem>
								<MenuItem value={300}>300 - Some Other Group</MenuItem> */}
							</Select>
						</FormControl>
					</Box>
					<Typography variant="h6">
						Select Permissions for{" "}
						<span
							style={{
								display: "inline-block",
								padding: "4px 12px",
								backgroundColor: "#e6f4ea",
								color: "#359a4c",
								fontWeight: "bold",
								borderRadius: "20px"
							}}
						>
							{displayName}
						</span>
					</Typography>

					<IconButton onClick={handlePermissionModelCloseClick}>
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent dividers style={{ maxHeight: 600 }}>
				<Box mb={2}>
					<TextField
						label="Search permissions"
						fullWidth
						variant="outlined"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</Box>
				{filteredTree.map((node) => renderRecursive(node))}
			</DialogContent>

			<Box p={2} display="flex" justifyContent="flex-end" gap={2}>
				<Button onClick={onClose}>Cancel</Button>
				<Button variant="contained" className="button-green" onClick={handleSave}>
					Save
				</Button>
			</Box>
		</Dialog>
	);
};

export default AssignPermissionDialog;
